// Base
import { BaseApi } from 'api/base.api';

// Interface
import {
  IAssessmentApi,
  IAssessmentApiRequest,
  IAssessmentApiGetListAttrs,
  IAssessmentApiListResponse,
  IAssessmentApiGetProgressDetailAttrs,
  IAssessmentApiProgressDetailResponse,
  IAssessmentApiGetDetailAttrs,
  IAssessmentApiDetailResponse,
  IAssessmentApiCancelAttrs,
  IAssessmentApiNullResponse,
  IAssessmentApiGetAssessmentPhaseListAttrs,
  IAssessmentApiAssessmentPhaseListResponse,
  IAssessmentApiGetAssessmentStartDetailAttrs,
  IAssessmentApiStartAssessmentDetailResponse,
  IAssessmentApiUpdateStartAssessmentDetailResponse,
  IAssessmentApiSubmitStartAssessmentDetailResponse,
  IAssessmentApiDecisionPhaseListResponse,
  IAssessmentApiFilterPhaseListResponse,
} from 'interfaces/back-office/assessment/assessment-api.interface';

// Constant
import { ApiConstantHc, ApiConstantHcBase } from 'constant';

// Utils
import { axios_renderHcUrl } from 'utils';
import {
  IAssessmentDecisionFormListAttrs,
  IAssessmentDecisionSubmitAttrs,
  IAssessmentFilterPhaseListAttrs,
  IAssessmentFilterStatusListAttrs,
  // IAssessmentGetAssessmentStartDetailAttrs,
  IAssessmentSubmitAssessmentStartDetailAttrs,
  IAssessmentUpdateAssessmentStartDetailAttrs,
} from 'interfaces/back-office/assessment/assessment-view.interface';

export class AssessmentApi extends BaseApi<IAssessmentApi> implements IAssessmentApiRequest {
  readonly url = ApiConstantHc.ASSESSMENT_URL;
  readonly urlcCms = ApiConstantHc.CMS_DECISION_URL;
  // assessments & assessments/api
  private assessmentUrl = axios_renderHcUrl(this.url, ApiConstantHcBase.ASSESSMENT);
  private cmsDecisionUrl = axios_renderHcUrl(this.urlcCms, ApiConstantHcBase.ASSESSMENT);

  /**
   * @description Get assessment list
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentApiGetListAttrs} payload
   *
   * @return {Promise<IAssessmentApiListResponse>} Promise<IAssessmentApiListResponse>
   */
  async assessment_getList(payload: IAssessmentApiGetListAttrs): Promise<IAssessmentApiListResponse> {
    return this.POST(`${this.assessmentUrl}/list`, payload.body, { ...payload.requestConfig });
  }

  /**
   * @description Get assessment progress detail
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentApiGetProgressDetailAttrs} payload
   *
   * @return {Promise<IAssessmentApiProgressDetailResponse>} Promise<IAssessmentApiProgressDetailResponse>
   */
  async assessment_getProgressDetail(
    payload: IAssessmentApiGetProgressDetailAttrs,
  ): Promise<IAssessmentApiProgressDetailResponse> {
    return this.POST(`${this.assessmentUrl}/progressDetail/${payload.params.assessmentId}`, {}, { ...payload.requestConfig });
  }

  /**
   * @description Get assessment detail
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentApiGetDetailAttrs} payload
   *
   * @return {Promise<IAssessmentApiDetailResponse>} Promise<IAssessmentApiDetailResponse>
   */
  async assessment_getDetail(payload: IAssessmentApiGetDetailAttrs): Promise<IAssessmentApiDetailResponse> {
    return this.POST(`${this.assessmentUrl}/${payload.params.assessmentId}`, {}, { ...payload.requestConfig });
  }

  /**
   * @description Cancel assessment
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentApiCancelAttrs} payload
   *
   * @return {Promise<IAssessmentApiNullResponse>} Promise<IAssessmentApiNullResponse>
   */
  async assessment_cancel(payload: IAssessmentApiCancelAttrs): Promise<IAssessmentApiNullResponse> {
    return this.POST(`${this.assessmentUrl}/cancel/${payload.params.assessmentId}`, {}, { ...payload.requestConfig });
  }

  /**
   * @description get Assessment list in assessment phase
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentApiGetAssessmentPhaseListAttrs} payload
   *
   * @return {Promise<IAssessmentApiAssessmentPhaseListResponse>} Promise<IAssessmentApiAssessmentPhaseListResponse>
   */
  async assessment_list(payload: IAssessmentApiGetAssessmentPhaseListAttrs): Promise<IAssessmentApiAssessmentPhaseListResponse> {
    return this.POST(`${this.assessmentUrl}/assessment-list/${payload.params.assessmentId}`, {}, { ...payload.requestConfig });
  }

  /**
   * @description start assessment, get Assessment question list in assessment phase
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentApiGetAssessmentStartDetailAttrs} payload
   *
   * @return {Promise<IAssessmentApiStartAssessmentDetailResponse>} Promise<IAssessmentApiStartAssessmentDetailResponse>
   */
  async assessment_start_detail(
    payload: IAssessmentApiGetAssessmentStartDetailAttrs,
  ): Promise<IAssessmentApiStartAssessmentDetailResponse> {
    return this.POST(`${this.assessmentUrl}/assessment-start/${payload.params.assessmentId}`, {}, { ...payload.requestConfig });
  }

  /**
   * @description update assessment answer in assessment phase
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentUpdateAssessmentStartDetailAttrs} payload
   *
   * @return {Promise<IAssessmentApiUpdateStartAssessmentDetailResponse>} Promise<IAssessmentApiUpdateStartAssessmentDetailResponse>
   */
  async assessment_update_start_detail(
    payload: IAssessmentUpdateAssessmentStartDetailAttrs,
  ): Promise<IAssessmentApiUpdateStartAssessmentDetailResponse> {
    return this.POST(
      `${this.assessmentUrl}/set-answer/${payload.params.assessmentId}`,
      {
        ...payload.body,
      },
      { ...payload.requestConfig },
    );
  }
  /**
   * @description send assessment in assessment phase
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentSubmitAssessmentStartDetailAttrs} payload
   *
   * @return {Promise<IAssessmentApiSubmitStartAssessmentDetailResponse>} Promise<IAssessmentApiSubmitStartAssessmentDetailResponse>
   */
  async assessment_send_start_detail(
    payload: IAssessmentSubmitAssessmentStartDetailAttrs,
  ): Promise<IAssessmentApiSubmitStartAssessmentDetailResponse> {
    return this.POST(`${this.assessmentUrl}/assessment-send/${payload.params.assessmentId}`, {}, { ...payload.requestConfig });
  }
  /**
   * @description submit assessment in assessment phase
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentSubmitAssessmentStartDetailAttrs} payload
   *
   * @return {Promise<IAssessmentApiSubmitStartAssessmentDetailResponse>} Promise<IAssessmentApiSubmitStartAssessmentDetailResponse>
   */
  async assessment_submit_assessment_phase(
    payload: IAssessmentSubmitAssessmentStartDetailAttrs,
  ): Promise<IAssessmentApiSubmitStartAssessmentDetailResponse> {
    return this.POST(`${this.assessmentUrl}/assessment-submit/${payload.params.assessmentId}`, {}, { ...payload.requestConfig });
  }
  /**
   * @description decision phase form list
   *
   * @method GET
   * @access private (only user that logged in)
   *
   * @param {IAssessmentSubmitAssessmentStartDetailAttrs} payload
   *
   * @return {Promise<IAssessmentApiSubmitStartAssessmentDetailResponse>} Promise<IAssessmentApiSubmitStartAssessmentDetailResponse>
   */
  async assessment_decision_form_list(
    payload: IAssessmentDecisionFormListAttrs,
  ): Promise<IAssessmentApiDecisionPhaseListResponse> {
    return this.GET(
      `${this.cmsDecisionUrl}/form?transAssId=${payload.query.transAssId}&assPhaseName=${payload.query.assPhaseName}`,
      { ...payload.requestConfig },
    );
  }
  /**
   * @description decision submit
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentDecisionSubmitAttrs} payload
   *
   * @return {Promise<IAssessmentApiSubmitStartAssessmentDetailResponse>} Promise<IAssessmentApiSubmitStartAssessmentDetailResponse>
   */
  async assessment_decision_submit(
    payload: IAssessmentDecisionSubmitAttrs,
  ): Promise<IAssessmentApiSubmitStartAssessmentDetailResponse> {
    return this.POST(`${this.cmsDecisionUrl}/decision/submit/${payload.params.assessmentId}`, { ...payload.requestConfig });
  }
  /**
   * @description fetch filter phase list
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentFilterPhaseListAttrs} payload
   *
   * @return {Promise<IAssessmentApiFilterPhaseListResponse>} Promise<IAssessmentApiFilterPhaseListResponse>
   */
  async assessment_filter_phase_list(payload: IAssessmentFilterPhaseListAttrs): Promise<IAssessmentApiFilterPhaseListResponse> {
    return this.POST(`${this.assessmentUrl}/phase-list`, { ...payload.requestConfig });
  }
  /**
   * @description fetch filter status list
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {IAssessmentFilterStatusListAttrs} payload
   *
   * @return {Promise<IAssessmentApiFilterPhaseListResponse>} Promise<IAssessmentApiFilterPhaseListResponse>
   */
  async assessment_filter_status_list(payload: IAssessmentFilterStatusListAttrs): Promise<IAssessmentApiFilterPhaseListResponse> {
    return this.POST(`${this.assessmentUrl}/phaseStatus-list/${payload.params.AssPhaseId}`, { ...payload.requestConfig });
  }
}
